import { forwardRef, useEffect, useRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export interface GWPCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
	size?: 'sm' | 'md' | 'lg';
	label?: React.ReactNode;
	invalid?: boolean;
	indeterminate?: boolean;
	onCheckedChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

const GWPCheckbox = forwardRef<HTMLInputElement, GWPCheckboxProps>((props, ref) => {
	const {
		className,
		size = 'md',
		label,
		invalid = false,
		indeterminate = false,
		onChange,
		onCheckedChange,
		disabled,
		...rest
	} = props;

	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (inputRef.current !== null) {
			inputRef.current.indeterminate = indeterminate;
		}
	}, [indeterminate]);

	const setInputRef = (node: HTMLInputElement | null) => {
		inputRef.current = node;

		if (typeof ref === 'function') {
			ref(node);
			return;
		}

		if (ref !== null) {
			ref.current = node;
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange?.(event);
		onCheckedChange?.(event.target.checked, event);
	};

	const rootClassName = combineClassNames(
		'gwp-checkbox',
		size,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		indeterminate ? 'indeterminate' : undefined,
		className,
	);

	return (
		<label className={rootClassName}>
			<input
				{...rest}
				ref={setInputRef}
				type="checkbox"
				disabled={disabled}
				aria-invalid={invalid || rest['aria-invalid']}
				className="gwp-checkbox-control"
				onChange={handleChange}
			/>
			{label !== undefined && <span className="gwp-checkbox-label">{label}</span>}
		</label>
	);
});

GWPCheckbox.displayName = 'GWPCheckbox';

export default GWPCheckbox;
